/**
 * SeedDrive V2 — Central Mock Data
 *
 * All demo/illustrative content lives here.
 * Components and pages import from this file rather than defining inline.
 *
 * Rules:
 * - Mock data only. No navigation structure, route logic, or UI state here.
 * - All paths used for navigation must be imported from config/navConfig.js.
 * - Easy to swap out for live API data later — each export maps to a clear domain.
 *
 * Cross-reference ID map:
 *   DEALS (pipelineData.js)  deal-1 … deal-12
 *   MOCK_IC_SUBMISSIONS      ic-solano, ic-porto, ic-nordic, ic-elbe, ic-lisbon, ic-tyne
 *   MOCK_ASSETS              solano-wind, meridian-solar, tyne-bridge, avon-water,
 *                            porto-digital, elbe-biomass, lisbon-metro, nordic-fibre
 *   MOCK_SPVS                spv-solano, spv-meridian, spv-elbe,
 *                            spv-tyne, spv-avon, spv-porto, spv-iberian, spv-lisbon, spv-nordic
 *   MOCK_FUNDS               fund-1, fund-2
 *   assetDataId field maps MOCK_ASSETS → assetData.js ASSETS (AST-001 … AST-008)
 */

import { ROUTES } from 'config/navConfig';
import { IC_STATES, MARKETPLACE_STATES, DEAL_PIPELINE_STATES } from 'config/workflowStates';

// ─── User Profile ─────────────────────────────────────────────────────────────

export const MOCK_USER = {
  name: 'James Donovan',
  email: 'j.donovan@seeddrive.com',
  initials: 'JD',
  role: 'investment_team',
};

// ─── Assets ───────────────────────────────────────────────────────────────────
// dealId:         links to originating deal in pipelineData.js DEALS
// icSubmissionId: links to the IC submission that approved this asset
// opportunityId:  links to the marketplace opportunity listing
// assetDataId:    links to assetData.js ASSETS (AST-00x) — explicit ID cross-ref

export const MOCK_ASSETS = [
  { id: 'solano-wind',    label: 'Solano Wind Farm',           subtitle: 'Renewable Energy · Spain',            path: ROUTES.operationsConsole, sector: 'Renewable Energy',       country: 'Spain',     dealId: 'deal-8',  icSubmissionId: 'ic-solano', opportunityId: 'solano-wind-opp',    assetDataId: 'AST-001' },
  { id: 'meridian-solar', label: 'Meridian Solar Park',        subtitle: 'Solar Energy · Portugal',             path: ROUTES.operationsConsole, sector: 'Solar Energy',           country: 'Portugal',  dealId: 'deal-9',  icSubmissionId: null,        opportunityId: null,                 assetDataId: 'AST-002' },
  { id: 'tyne-bridge',    label: 'Tyne Bridge Toll Road',      subtitle: 'Transport Infrastructure · UK',       path: ROUTES.operationsConsole, sector: 'Transport',              country: 'UK',        dealId: 'deal-7',  icSubmissionId: 'ic-tyne',   opportunityId: null,                 assetDataId: 'AST-003' },
  { id: 'avon-water',     label: 'Avon Water Treatment Works', subtitle: 'Utilities · UK',                      path: ROUTES.operationsConsole, sector: 'Utilities',              country: 'UK',        dealId: 'deal-6',  icSubmissionId: null,        opportunityId: null,                 assetDataId: 'AST-004' },
  { id: 'porto-digital',  label: 'Porto Digital Hub',          subtitle: 'Digital Infrastructure · Portugal',   path: ROUTES.operationsConsole, sector: 'Digital Infrastructure', country: 'Portugal',  dealId: 'deal-10', icSubmissionId: 'ic-porto',  opportunityId: 'porto-digital-opp',  assetDataId: 'AST-005' },
  { id: 'elbe-biomass',   label: 'Elbe Biomass Plant',         subtitle: 'Renewable Energy · Germany',          path: ROUTES.operationsConsole, sector: 'Renewable Energy',       country: 'Germany',   dealId: 'deal-3',  icSubmissionId: 'ic-elbe',   opportunityId: 'elbe-biomass-opp',   assetDataId: 'AST-006' },
  { id: 'lisbon-metro',   label: 'Lisbon Metro Extension',     subtitle: 'Transport Infrastructure · Portugal', path: ROUTES.operationsConsole, sector: 'Transport',              country: 'Portugal',  dealId: 'deal-4',  icSubmissionId: 'ic-lisbon', opportunityId: 'lisbon-metro-opp',   assetDataId: 'AST-007' },
  { id: 'nordic-fibre',   label: 'Nordic Fibre Network',       subtitle: 'Digital Infrastructure · Nordics',    path: ROUTES.operationsConsole, sector: 'Digital Infrastructure', country: 'Nordics',   dealId: 'deal-5',  icSubmissionId: 'ic-nordic', opportunityId: 'nordic-fibre-opp',   assetDataId: 'AST-008' },
];

// ─── Notification badge counts ────────────────────────────────────────────────

export const NOTIFICATION_COUNTS = {
  [ROUTES.messages]:      3,
  [ROUTES.notifications]: 12,
};

// ─── Marketplace Opportunities ────────────────────────────────────────────────
// marketplaceState: the active demo investor's current access state for this opportunity.
// Used by OpportunitiesListPage + ExternalInvestor deal room journey.

export const MOCK_OPPORTUNITIES = [
  {
    id: 'solano-wind-opp',
    name: 'Solano Wind Farm SPV',
    sector: 'Renewable Energy',
    geography: 'Spain',
    targetSize: '£85m',
    targetIRR: '12.4%',
    closingDate: '30/06/2026',
    status: 'committed',
    marketplaceState: MARKETPLACE_STATES.COMMITTED,
    description: 'Onshore wind farm in Andalusia — 140MW capacity. Fully deployed in Fund I.',
    highlights: ['140MW capacity', 'PPA secured', 'First close achieved', 'FCA regulated SPV'],
    vehicleType: 'Limited Partnership',
    minTicket: '£5m',
  },
  {
    id: 'porto-digital-opp',
    name: 'Porto Digital Hub SPV',
    sector: 'Digital Infrastructure',
    geography: 'Portugal',
    targetSize: '£45m',
    targetIRR: '14.1%',
    closingDate: '30/09/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.NDA_PENDING,
    description: 'Tier III data centre campus serving the Iberian Peninsula. Anchor tenants contracted.',
    highlights: ['Tier III certification', 'Anchor tenants signed', 'PEI-aligned reporting', 'EUR-denominated returns'],
    vehicleType: 'Limited Partnership',
    minTicket: '£3m',
  },
  {
    id: 'nordic-fibre-opp',
    name: 'Nordic Fibre Network SPV',
    sector: 'Digital Infrastructure',
    geography: 'Nordics',
    targetSize: '£120m',
    targetIRR: '11.8%',
    closingDate: '31/03/2027',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.LISTED,
    description: 'National fibre broadband rollout programme across Sweden and Denmark. Backed by development finance.',
    highlights: ['DFI co-investment', 'Regulated revenue', '25-year concession', 'ESG impact reporting'],
    vehicleType: 'Infrastructure Fund',
    minTicket: '£10m',
  },
  {
    id: 'elbe-biomass-opp',
    name: 'Elbe Biomass Plant SPV',
    sector: 'Renewable Energy',
    geography: 'Germany',
    targetSize: '£60m',
    targetIRR: '13.5%',
    closingDate: '30/06/2026',
    status: 'closing',
    marketplaceState: MARKETPLACE_STATES.COMMITTED,
    description: 'Biomass-to-energy plant in Lower Saxony. Government subsidy underpinned. Final close imminent.',
    highlights: ['Government subsidy', 'Final close Q2 2026', 'Operational asset', '20-year offtake'],
    vehicleType: 'Special Purpose Vehicle',
    minTicket: '£2m',
  },
  {
    id: 'lisbon-metro-opp',
    name: 'Lisbon Metro Extension',
    sector: 'Transport Infrastructure',
    geography: 'Portugal',
    targetSize: '£200m',
    targetIRR: '10.2%',
    closingDate: '31/12/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.ACCESS_REQUESTED,
    description: 'Public-private partnership for 12km metro extension in Greater Lisbon. Sovereign-backed concession.',
    highlights: ['Sovereign-backed', 'PPP concession', '30-year term', 'EUR revenues'],
    vehicleType: 'PPP Concession',
    minTicket: '£15m',
  },
  {
    id: 'avon-water-opp',
    name: 'Avon Water Treatment Works Phase 3',
    sector: 'Water & Utilities',
    geography: 'South West England',
    targetSize: '£48m',
    targetIRR: '9.1%',
    closingDate: '31/12/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.LISTED,
    description: 'AMP8 capital programme for regulated water treatment capacity expansion. RPI-linked revenues.',
    highlights: ['Ofwat-regulated', 'RPI revenues', 'AMP8 funded', 'UK pension-grade'],
    vehicleType: 'Limited Partnership',
    minTicket: '£5m',
  },
  {
    id: 'tyne-bridge-refi-opp',
    name: 'Tyne Bridge Toll Road — Refinancing',
    sector: 'Transport Infrastructure',
    geography: 'North East England',
    targetSize: '£120m',
    targetIRR: '7.2%',
    closingDate: '30/09/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.ACCESS_REQUESTED,
    description: 'Long-dated senior refinancing of operational tolled crossing. Investment grade rating. 25-year remaining concession term.',
    highlights: ['Investment grade', 'Operational asset', '25-year remaining term', 'Traffic covenant'],
    vehicleType: 'Special Purpose Vehicle',
    minTicket: '£10m',
  },
  {
    id: 'iberian-storage-opp',
    name: 'Iberian Battery Storage Complex',
    sector: 'Energy Transition',
    geography: 'Spain',
    targetSize: '£90m',
    targetIRR: '13.8%',
    closingDate: '30/09/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.DD_ACCESS,
    description: '800MWh grid-scale BESS in Catalonia. Capacity market revenues and merchant optionality.',
    highlights: ['800MWh capacity', 'Capacity market contract', 'Grid-scale', 'Spanish energy transition'],
    vehicleType: 'Infrastructure Fund',
    minTicket: '£7.5m',
    coInvestmentTerms: {
      gpCommitment: '£10m (11.1%)',
      coInvestorAllocation: '£20m (22.2%)',
      carry: '15% above 8% hurdle',
      preferredReturn: '8% p.a. cumulative',
      managementFee: '1.5% on committed capital',
      transferRestrictions: 'Lock-up 3 years; GP consent required',
      governanceRights: 'Observer seat on SPV Advisory Board',
      closingDate: '30/09/2026',
    },
  },
  {
    id: 'east-anglia-capture-opp',
    name: 'East Anglia Carbon Capture — Phase I',
    sector: 'Energy Transition',
    geography: 'East Anglia, UK',
    targetSize: '£75m',
    targetIRR: '14.2%',
    closingDate: '31/03/2027',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.LISTED,
    description: 'Industrial carbon capture and storage project. BECCS technology. UK Government CCS cluster support.',
    highlights: ['BECCS technology', 'Government-backed', 'Carbon credit upside', 'NZE-aligned'],
    vehicleType: 'Special Purpose Vehicle',
    minTicket: '£5m',
  },
  {
    id: 'meridian-solar-ext-opp',
    name: 'Meridian Solar Park — Extension',
    sector: 'Renewable Energy',
    geography: 'Portugal',
    targetSize: '£62m',
    targetIRR: '9.8%',
    closingDate: '31/08/2026',
    status: 'open',
    marketplaceState: MARKETPLACE_STATES.NDA_PENDING,
    description: 'Co-location expansion adjacent to operational 180MW facility. PPA renewal in progress. Grid connection secured.',
    highlights: ['Co-location advantage', 'Operational neighbour', 'Grid connection secured', 'PPA term reset'],
    vehicleType: 'Limited Partnership',
    minTicket: '£5m',
  },
];

// ─── IC Submissions ───────────────────────────────────────────────────────────
// pipelineDealId: links to DEALS in pipelineData.js
// assetId:        links to MOCK_ASSETS
// opportunityId:  links to MOCK_OPPORTUNITIES

export const MOCK_IC_SUBMISSIONS = [
  {
    id: 'ic-solano',
    dealName: 'Solano Wind Farm SPV',
    sector: 'Renewable Energy',
    jurisdiction: 'England & Wales',
    dealSize: '£85m',
    targetIRR: '12.4%',
    submittedBy: 'J. Donovan',
    submittedDate: '14/02/2026',
    icDate: '28/02/2026',
    leadAnalyst: 'R. Chen',
    icState: IC_STATES.APPROVED,
    conditions: null,
    opportunityId: 'solano-wind-opp',
    assetId: 'solano-wind',
    pipelineDealId: 'deal-8',
  },
  {
    id: 'ic-porto',
    dealName: 'Porto Digital Hub SPV',
    sector: 'Digital Infrastructure',
    jurisdiction: 'England & Wales',
    dealSize: '£45m',
    targetIRR: '14.1%',
    submittedBy: 'S. Patel',
    submittedDate: '01/03/2026',
    icDate: '15/03/2026',
    leadAnalyst: 'A. Williams',
    icState: IC_STATES.UNDER_REVIEW,
    conditions: null,
    opportunityId: 'porto-digital-opp',
    assetId: 'porto-digital',
    pipelineDealId: 'deal-10',
  },
  {
    id: 'ic-nordic',
    dealName: 'Nordic Fibre Network SPV',
    sector: 'Digital Infrastructure',
    jurisdiction: 'England & Wales',
    dealSize: '£120m',
    targetIRR: '11.8%',
    submittedBy: 'J. Donovan',
    submittedDate: '05/03/2026',
    icDate: '20/03/2026',
    leadAnalyst: 'R. Chen',
    icState: IC_STATES.SUBMITTED,
    conditions: null,
    opportunityId: 'nordic-fibre-opp',
    assetId: 'nordic-fibre',
    pipelineDealId: 'deal-5',
  },
  {
    id: 'ic-elbe',
    dealName: 'Elbe Biomass Plant SPV',
    sector: 'Renewable Energy',
    jurisdiction: 'England & Wales',
    dealSize: '£60m',
    targetIRR: '13.5%',
    submittedBy: 'S. Patel',
    submittedDate: '10/01/2026',
    icDate: '24/01/2026',
    leadAnalyst: 'O. Chen',
    icState: IC_STATES.APPROVED_WITH_CONDITIONS,
    conditions: 'Subject to satisfactory legal review of German subsidy framework and final LPA sign-off.',
    opportunityId: 'elbe-biomass-opp',
    assetId: 'elbe-biomass',
    pipelineDealId: 'deal-3',
  },
  {
    id: 'ic-lisbon',
    dealName: 'Lisbon Metro Extension',
    sector: 'Transport Infrastructure',
    jurisdiction: 'England & Wales',
    dealSize: '£200m',
    targetIRR: '10.2%',
    submittedBy: 'J. Donovan',
    submittedDate: '07/03/2026',
    icDate: '25/03/2026',
    leadAnalyst: 'A. Williams',
    icState: IC_STATES.DRAFT,
    conditions: null,
    opportunityId: 'lisbon-metro-opp',
    assetId: 'lisbon-metro',
    pipelineDealId: 'deal-4',
  },
  {
    id: 'ic-tyne',
    dealName: 'Tyne Bridge Refinancing',
    sector: 'Transport Infrastructure',
    jurisdiction: 'England & Wales',
    dealSize: '£120m',
    targetIRR: '7.2%',
    submittedBy: 'T. Hargreaves',
    submittedDate: '02/04/2026',
    icDate: '13/04/2026',
    leadAnalyst: 'T. Hargreaves',
    icState: IC_STATES.UNDER_REVIEW,
    conditions: null,
    riskFlags: 'Construction timeline carries 3-month buffer risk given current contractor capacity constraints in the North East. Projected capex overrun of £2.1m (4.4% of total) within tolerable threshold; sponsor has indicated contingency is available.',
    conditionsDraft: 'Approval conditional on: (1) Independent technical review confirming contractor programme revised schedule within 30 days of IC; (2) Debt facility term sheet from lead arranger received prior to SPV formation.',
    committeeMembers: [
      { name: 'Sarah Chen',    role: 'IC Chair',                    initials: 'SC', vote: 'approved', votedAt: '2026-04-09' },
      { name: 'Tom Whitfield', role: 'Sector Lead — Transport',     initials: 'TW', vote: 'approved', votedAt: '2026-04-10' },
      { name: 'James Donovan', role: 'Portfolio Director',          initials: 'JD', vote: 'pending',  votedAt: null },
      { name: 'Rachel Moore',  role: 'Risk & Compliance',           initials: 'RM', vote: 'pending',  votedAt: null },
    ],
    opportunityId: null,
    assetId: 'tyne-bridge',
    pipelineDealId: 'deal-7',
  },
];

// ─── Funds ────────────────────────────────────────────────────────────────────
// Top-level fund entities. SPVs and assets belong to a fund.

export const MOCK_FUNDS = [
  {
    id: 'fund-1',
    name: 'Greenfield Renewables Fund I',
    shortName: 'Fund I',
    vintage: 2024,
    targetSize: 200_000_000,
    closedSize: 200_000_000,
    status: 'Closed',
    // Fund governance & structure
    fundManager: 'Greenfield Capital Partners LLP',
    fundTerm: 10,                 // years
    investmentPeriod: 5,          // years
    currency: 'GBP',
    domicile: 'England & Wales',
    regulatoryStatus: 'FCA Authorised',
    managementFee: 1.5,           // % p.a. on committed capital
    carry: 20,                    // % carried interest
    preferredReturn: 8,           // % hurdle rate
    inceptionDate: '01/01/2024',
    firstCloseDate: '31/01/2025',
    finalCloseDate: '30/06/2025',
    // Fund-level performance (computed by GP / fund admin)
    netIRR: 11.2,
    grossIRR: 13.8,
    tvpiMultiple: 1.18,
    dpiMultiple: 0.06,
    rvpiMultiple: 1.12,
    nav: 175_500_000,
    spvIds: ['spv-solano', 'spv-meridian', 'spv-elbe'],
    lpCommitments: [
      { id: 'lp-f1-1', name: 'Nordic Pension Fund',            type: 'Pension Fund',         commitmentAmount: 50_000_000, calledAmount: 47_500_000, distributedAmount: 2_800_000, status: 'closed' },
      { id: 'lp-f1-2', name: 'Abu Dhabi Investment Authority', type: 'Sovereign Wealth Fund', commitmentAmount: 40_000_000, calledAmount: 38_000_000, distributedAmount: 2_200_000, status: 'closed' },
      { id: 'lp-f1-3', name: 'CDPQ',                          type: 'Pension Fund',         commitmentAmount: 35_000_000, calledAmount: 33_300_000, distributedAmount: 1_900_000, status: 'closed' },
      { id: 'lp-f1-4', name: 'European Investment Bank',       type: 'Multilateral Bank',    commitmentAmount: 25_000_000, calledAmount: 23_800_000, distributedAmount: 1_400_000, status: 'closed' },
      { id: 'lp-f1-5', name: 'GP Co-Commitment',               type: 'GP',                   commitmentAmount: 15_000_000, calledAmount: 14_300_000, distributedAmount:   800_000, status: 'closed' },
    ],
  },
  {
    id: 'fund-2',
    name: 'Greenfield Infrastructure Fund II',
    shortName: 'Fund II',
    vintage: 2025,
    targetSize: 350_000_000,
    closedSize: null,
    raisedSoftCircled: 140_000_000,
    status: 'Raising',
    // Fund governance & structure
    fundManager: 'Greenfield Capital Partners LLP',
    fundTerm: 10,
    investmentPeriod: 5,
    currency: 'GBP',
    domicile: 'England & Wales',
    regulatoryStatus: 'FCA Authorised',
    managementFee: 1.5,
    carry: 20,
    preferredReturn: 8,
    inceptionDate: '01/01/2025',
    firstCloseDate: null,
    finalCloseDate: null,
    // Performance — null until capital is deployed
    netIRR: null,
    grossIRR: null,
    tvpiMultiple: null,
    dpiMultiple: null,
    rvpiMultiple: null,
    nav: null,
    spvIds: ['spv-tyne', 'spv-avon', 'spv-porto', 'spv-iberian', 'spv-lisbon', 'spv-nordic'],
    lpCommitments: [
      { id: 'lp-f2-1', name: 'Nordic Pension Fund',            type: 'Pension Fund',         commitmentAmount: 80_000_000, calledAmount: 0, distributedAmount: 0, status: 'soft-circled' },
      { id: 'lp-f2-2', name: 'Abu Dhabi Investment Authority', type: 'Sovereign Wealth Fund', commitmentAmount: 60_000_000, calledAmount: 0, distributedAmount: 0, status: 'soft-circled' },
    ],
  },
];

// ─── SPVs ─────────────────────────────────────────────────────────────────────
// Centralised SPV entity records. Previously spv-solano data was inline in
// spv-dashboard/index.jsx — moved here for cross-module traceability.
//
// dealId:         links to originating pipeline deal (pipelineData.js)
// icSubmissionId: links to the IC submission that authorised SPV formation
// assetId:        links to MOCK_ASSETS / assetData.js
// fundId:         links to MOCK_FUNDS
//
// commitments: contextId field on Seplat entry enables ID-based isSelf
//              resolution in SPV Dashboard (no string-matching needed).

export const MOCK_SPVS = [
  {
    id: 'spv-solano',
    name: 'Solano Wind Infrastructure Ltd',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '14/01/2026',
    sector: 'Renewable Energy',
    targetIRR: '12.4%',
    targetSize: 85_000_000,
    raisedAmount: 57_500_000,
    investorCount: 7,
    closingDate: '30/06/2026',
    // Cross-module links
    dealId: 'deal-8',
    icSubmissionId: 'ic-solano',
    assetId: 'solano-wind',
    fundId: 'fund-1',
    assetDataId: 'AST-001',
    gpFundAllocation: 15_000_000,  // GP's committed capital from Fund I to this SPV
    assetSnapshot: {
      name: 'Solano Wind Farm',
      sector: 'Renewable Energy',
      geography: 'Andalusia, Spain',
      operationalStatus: 'Operational',
      actualIRR: 12.4,
      targetIRR: '12.4%',
      valuation: '£85.2m',
      valuationDate: 'Sep 2025',
      esgScore: 76,
      riskScore: 3,
    },
    capitalCall: {
      callNumber: 3,
      amount: 2_100_000,
      dueDate: '2026-04-30',
      status: 'issued',
      issuedDate: '2026-04-07',
    },
    commitments: [
      { id: 1, investor: 'Meridian Capital Partners',         initials: 'MC', amount: 12_000_000, called: 8_000_000,  distributed: 1_200_000, ownership: 20.9, status: 'confirmed', lastCallDate: '15/02/2026', commitmentType: 'co_investor' },
      { id: 2, investor: 'Albion Infrastructure Fund',        initials: 'AI', amount: 8_500_000,  called: 5_700_000,  distributed:   850_000, ownership: 14.8, status: 'confirmed', lastCallDate: '15/02/2026', commitmentType: 'co_investor' },
      { id: 3, investor: 'Sovereign Wealth Partners',         initials: 'SW', amount: 7_000_000,  called: 4_700_000,  distributed:         0, ownership: 12.2, status: 'called',    lastCallDate: '01/03/2026', commitmentType: 'co_investor' },
      { id: 4, investor: 'Apex Infrastructure Partners',      initials: 'AP', amount: 15_000_000, called: 7_500_000,  distributed:         0, ownership: 17.6, status: 'confirmed', lastCallDate: '07/04/2026', contextId: 'ctx-co-investor', commitmentType: 'co_investor' },
      { id: 5, investor: 'Caledonian Pension Trust',          initials: 'CP', amount: 5_000_000,  called: 3_400_000,  distributed:         0, ownership:  8.7, status: 'pending',   lastCallDate: '—', commitmentType: 'co_investor' },
      { id: 6, investor: 'Harrow DFI Mandate',                initials: 'HD', amount: 2_500_000,  called: 1_700_000,  distributed:   250_000, ownership:  4.3, status: 'confirmed', lastCallDate: '15/02/2026', commitmentType: 'co_investor' },
      { id: 7, investor: 'Westgate Endowment',                initials: 'WE', amount: 1_500_000,  called:   700_000,  distributed:         0, ownership:  2.6, status: 'pending',   lastCallDate: '—', commitmentType: 'co_investor' },
    ],
    documents: [
      { id: 1,  name: 'LPA – Greenfield Renewables SPV I',        ext: 'pdf',  size: '2.4 MB', updatedAt: '28/02/2026', version: '3.1', category: 'Legal',      access: 'accessible' },
      { id: 2,  name: 'Subscription Agreement Template',          ext: 'docx', size: '890 KB', updatedAt: '20/02/2026', version: '2.0', category: 'Legal',      access: 'accessible' },
      { id: 3,  name: 'Financial Model – Base Case',              ext: 'xlsx', size: '5.1 MB', updatedAt: '25/02/2026', version: '4.2', category: 'Financial',  access: 'accessible' },
      { id: 4,  name: 'IC Approval Memo',                         ext: 'pdf',  size: '1.1 MB', updatedAt: '10/01/2026', version: '1.0', category: 'Governance', access: 'restricted' },
      { id: 5,  name: 'ESG Impact Assessment',                    ext: 'pdf',  size: '3.2 MB', updatedAt: '05/02/2026', version: '1.3', category: 'ESG',        access: 'accessible' },
      { id: 6,  name: 'Capital Call Notice – Feb 2026',           ext: 'pdf',  size: '420 KB', updatedAt: '14/02/2026', version: '1.0', category: 'Financial',  access: 'accessible' },
      { id: 7,  name: 'Investor Deck v4',                         ext: 'pptx', size: '8.7 MB', updatedAt: '12/01/2026', version: '4.0', category: 'Marketing',  access: 'accessible' },
      { id: 8,  name: 'AML & KYC Register',                       ext: 'xlsx', size: '650 KB', updatedAt: '01/03/2026', version: '2.1', category: 'Compliance', access: 'restricted' },
      { id: 9,  name: 'Q1 2026 Co-Investor Report',               ext: 'pdf',  size: '1.8 MB', updatedAt: '07/04/2026', version: '1.0', category: 'Financial',  access: 'accessible' },
      { id: 10, name: 'Capital Call Notice No. 3 — Q1 2026',      ext: 'pdf',  size: '380 KB', updatedAt: '07/04/2026', version: '1.0', category: 'Financial',  access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Obtain final LP signatures for Caledonian Pension Trust', priority: 'high',   status: 'in-progress', assignee: 'J. Donovan',  dueDate: '10/03/2026' },
      { id: 2, title: 'Issue capital call notice to Westgate Endowment',         priority: 'high',   status: 'todo',        assignee: 'S. Patel',    dueDate: '12/03/2026' },
      { id: 3, title: 'Update financial model with Q1 actuals',                  priority: 'medium', status: 'in-progress', assignee: 'R. Chen',     dueDate: '15/03/2026' },
      { id: 4, title: 'Prepare ESG quarterly report',                            priority: 'medium', status: 'todo',        assignee: 'A. Williams', dueDate: '20/03/2026' },
      { id: 5, title: 'Review and approve AML register update',                  priority: 'high',   status: 'blocked',     assignee: 'J. Donovan',  dueDate: '08/03/2026' },
      { id: 6, title: 'Schedule IC review for secondary close',                  priority: 'low',    status: 'todo',        assignee: 'S. Patel',    dueDate: '25/03/2026' },
      { id: 7, title: 'Send distribution notice to confirmed LPs',               priority: 'medium', status: 'done',        assignee: 'R. Chen',     dueDate: '28/02/2026' },
      { id: 8, title: 'Tokenisation readiness assessment',                       priority: 'low',    status: 'todo',        assignee: 'A. Williams', dueDate: '31/03/2026' },
    ],
    milestones: [
      { label: 'First Close',       date: '31/01/2026', done: true  },
      { label: 'Second Close',      date: '31/03/2026', done: false },
      { label: 'Final Close',       date: '30/06/2026', done: false },
      { label: 'First Capital Call',date: '15/02/2026', done: true  },
      { label: 'Asset Acquisition', date: '30/09/2026', done: false },
    ],
  },
  {
    id: 'spv-tyne',
    name: 'Tyne Bridge Refinancing SPV',
    status: 'pre-formation',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: null,
    sector: 'Transport Infrastructure',
    targetIRR: '7.2%',
    targetSize: 120_000_000,
    raisedAmount: 0,
    investorCount: 0,
    closingDate: 'Subject to IC approval — IC meeting 13 April 2026',
    icPendingBanner: true,
    // Cross-module links
    dealId: 'deal-7',
    icSubmissionId: 'ic-tyne',
    assetId: 'tyne-bridge',
    fundId: 'fund-2',
    assetDataId: 'AST-003',
    gpFundAllocation: null,  // not yet allocated — pending IC approval
    assetSnapshot: null,
    // Anchor investors: soft-circled pre-IC indicative interest — NOT formal commitments
    commitments: [
      { id: 1, investor: 'Meridian Capital Partners',  initials: 'MC', amount: 35_000_000, called: 0, distributed: 0, ownership: null, status: 'soft-circled', lastCallDate: '—', contextId: null, commitmentType: 'co_investor' },
      { id: 2, investor: 'Albion Infrastructure Fund', initials: 'AI', amount: 25_000_000, called: 0, distributed: 0, ownership: null, status: 'soft-circled', lastCallDate: '—', contextId: null, commitmentType: 'co_investor' },
      { id: 3, investor: 'Nordic Capital Partners',    initials: 'NC', amount: 20_000_000, called: 0, distributed: 0, ownership: null, status: 'soft-circled', lastCallDate: '—', contextId: null, commitmentType: 'co_investor' },
    ],
    documents: [
      { id: 1, name: 'Draft Limited Partnership Agreement',  ext: 'pdf',  size: '1.4 MB', updatedAt: '02/04/2026', version: '0.1', category: 'Legal',      access: 'restricted' },
      { id: 2, name: 'Investor Presentation — Pre-IC',       ext: 'pdf',  size: '3.2 MB', updatedAt: '01/04/2026', version: '0.2', category: 'Marketing',  access: 'restricted' },
      { id: 3, name: 'Fund Structure Diagram',               ext: 'pdf',  size: '620 KB', updatedAt: '30/03/2026', version: '0.1', category: 'Legal',      access: 'restricted' },
      { id: 4, name: 'Draft Articles of Association',        ext: 'docx', size: '890 KB', updatedAt: '28/03/2026', version: '0.2', category: 'Legal',      access: 'restricted' },
      { id: 5, name: 'Subscription Form Template',           ext: 'docx', size: '420 KB', updatedAt: '25/03/2026', version: '0.1', category: 'Legal',      access: 'restricted' },
      { id: 6, name: 'Legal Opinions — Jurisdiction Review', ext: 'pdf',  size: '1.1 MB', updatedAt: '20/03/2026', version: '0.1', category: 'Legal',      access: 'restricted' },
    ],
    tasks: [
      { id: 1, title: 'IC approval decision — Tyne Bridge Refinancing',          priority: 'high',   status: 'in-progress', assignee: 'T. Hargreaves', dueDate: '13/04/2026' },
      { id: 2, title: 'Finalise LP agreement template with legal counsel',        priority: 'medium', status: 'todo',        assignee: 'S. Patel',      dueDate: '20/04/2026' },
      { id: 3, title: 'Obtain debt facility term sheet from lead arranger',       priority: 'high',   status: 'todo',        assignee: 'T. Hargreaves', dueDate: '13/05/2026' },
      { id: 4, title: 'Independent technical review — contractor revised schedule', priority: 'high', status: 'todo',        assignee: 'J. Donovan',    dueDate: '13/05/2026' },
      { id: 5, title: 'Convert soft-circled anchors to formal commitments',       priority: 'medium', status: 'todo',        assignee: 'J. Donovan',    dueDate: '30/04/2026' },
    ],
    milestones: [
      { label: 'IC Submission Lodged', date: '02/04/2026', done: true  },
      { label: 'IC Approval',          date: '13/04/2026', done: false },
      { label: 'LPA Signed',           date: 'TBC',        done: false },
      { label: 'First Close',          date: 'TBC',        done: false },
      { label: 'SPV Active',           date: 'TBC',        done: false },
    ],
  },
  {
    id: 'spv-iberian',
    name: 'Iberian Battery Storage SPV Ltd',
    status: 'fundraising',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '01/03/2026',
    sector: 'Energy Transition',
    targetIRR: '13.8%',
    targetSize: 90_000_000,
    raisedAmount: 38_000_000,
    investorCount: 4,
    closingDate: '30/09/2026',
    dealId: null,
    icSubmissionId: null,
    assetId: null,
    fundId: 'fund-2',
    assetDataId: null,
    gpFundAllocation: 10_000_000,  // GP's committed capital from Fund II to this SPV
    assetSnapshot: {
      name: 'Iberian Battery Storage Complex',
      sector: 'Energy Transition',
      geography: 'Catalonia, Spain',
      operationalStatus: 'Pre-Acquisition',
      actualIRR: null,
      targetIRR: '13.8%',
      valuation: '£90m (target equity)',
      valuationDate: 'Apr 2026',
      esgScore: null,
      riskScore: 2,
    },
    capitalCall: {
      callNumber: 1,
      amount: 4_000_000,
      dueDate: '2026-05-15',
      status: 'issued',
      issuedDate: '2026-04-10',
    },
    commitments: [
      { id: 1, investor: 'Greenfield GP Co-Commitment',  initials: 'GR', amount: 10_000_000, called: 2_000_000, distributed: 0, ownership: 11.1, status: 'confirmed',    lastCallDate: '10/04/2026', contextId: null, commitmentType: 'gp_allocation' },
      { id: 2, investor: 'Apex Infrastructure Partners', initials: 'AP', amount: 20_000_000, called: 4_000_000, distributed: 0, ownership: 22.2, status: 'confirmed',    lastCallDate: '10/04/2026', contextId: 'ctx-co-investor', commitmentType: 'co_investor' },
      { id: 3, investor: 'Meridian Capital Partners',    initials: 'MC', amount:  5_000_000, called: 1_000_000, distributed: 0, ownership:  5.6, status: 'called',       lastCallDate: '10/04/2026', contextId: null, commitmentType: 'co_investor' },
      { id: 4, investor: 'Nordic Pension Fund',          initials: 'NP', amount:  3_000_000, called:         0, distributed: 0, ownership:  3.3, status: 'soft-circled', lastCallDate: '—',          contextId: null, commitmentType: 'co_investor' },
    ],
    documents: [
      { id: 1, name: 'Draft LPA — Iberian Battery Storage SPV',       ext: 'pdf',  size: '1.8 MB', updatedAt: '25/03/2026', version: '0.3', category: 'Legal',      access: 'accessible' },
      { id: 2, name: 'Co-Investment Agreement — Apex Infrastructure',  ext: 'docx', size: '1.1 MB', updatedAt: '01/04/2026', version: '1.0', category: 'Legal',      access: 'accessible' },
      { id: 3, name: 'Financial Model — Base & Downside',              ext: 'xlsx', size: '4.3 MB', updatedAt: '28/03/2026', version: '2.1', category: 'Financial',  access: 'accessible' },
      { id: 4, name: 'IC Approval Memo',                               ext: 'pdf',  size: '980 KB', updatedAt: '15/03/2026', version: '1.0', category: 'Governance', access: 'restricted' },
      { id: 5, name: 'ESG & Capacity Market Assessment',               ext: 'pdf',  size: '2.7 MB', updatedAt: '20/03/2026', version: '1.1', category: 'ESG',        access: 'accessible' },
      { id: 6, name: 'Capital Call Notice No. 1 — Apr 2026',           ext: 'pdf',  size: '360 KB', updatedAt: '10/04/2026', version: '1.0', category: 'Financial',  access: 'accessible' },
      { id: 7, name: 'Investor Deck — Iberian BESS v2',                ext: 'pptx', size: '7.2 MB', updatedAt: '05/03/2026', version: '2.0', category: 'Marketing',  access: 'accessible' },
      { id: 8, name: 'Q1 2026 Co-Investment Update — Iberian Battery Storage', ext: 'pdf', size: '1.4 MB', updatedAt: '10/04/2026', version: '1.0', category: 'Financial', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Obtain signed Co-Investment Agreement from Apex',        priority: 'high',   status: 'in-progress', assignee: 'J. Donovan',  dueDate: '20/04/2026' },
      { id: 2, title: 'Issue first capital call to all confirmed LPs',          priority: 'high',   status: 'done',        assignee: 'S. Patel',    dueDate: '10/04/2026' },
      { id: 3, title: 'Update financial model with Spanish capacity market Q1', priority: 'medium', status: 'todo',        assignee: 'R. Chen',     dueDate: '25/04/2026' },
      { id: 4, title: 'Convert Nordic Pension Fund to confirmed',               priority: 'medium', status: 'todo',        assignee: 'J. Donovan',  dueDate: '30/04/2026' },
      { id: 5, title: 'Tokenisation readiness assessment',                      priority: 'low',    status: 'todo',        assignee: 'A. Williams', dueDate: '15/05/2026' },
    ],
    milestones: [
      { label: 'IC Approval',        date: '15/03/2026', done: true  },
      { label: 'SPV Established',    date: '01/03/2026', done: true  },
      { label: 'First Capital Call', date: '10/04/2026', done: true  },
      { label: 'First Close',        date: '30/06/2026', done: false },
      { label: 'Asset Acquisition',  date: '30/09/2026', done: false },
    ],
  },
  {
    id: 'spv-meridian',
    name: 'Meridian Solar Partners LP',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '12/06/2024',
    sector: 'Renewable Energy',
    targetIRR: '9.8%',
    targetSize: 62_000_000,
    raisedAmount: 62_000_000,
    investorCount: 3,
    closingDate: '12/06/2024',
    dealId: 'deal-9',
    icSubmissionId: null,
    assetId: 'meridian-solar',
    fundId: 'fund-1',
    assetDataId: 'AST-002',
    gpFundAllocation: null,  // no GP co-commitment on this SPV
    assetSnapshot: {
      name: 'Meridian Solar Park',
      sector: 'Renewable Energy',
      geography: 'Alentejo, Portugal',
      operationalStatus: 'Operational',
      actualIRR: 9.8,
      targetIRR: '9.8%',
      valuation: '£63.1m',
      valuationDate: 'Sep 2025',
      esgScore: 82,
      riskScore: 2,
    },
    commitments: [
      { id: 1, investor: 'Nordic Pension Fund',        initials: 'NP', amount: 20_000_000, called: 20_000_000, distributed: 1_200_000, ownership: 32.3, status: 'confirmed', lastCallDate: '01/07/2024', contextId: null, commitmentType: 'co_investor' },
      { id: 2, investor: 'Albion Infrastructure Fund', initials: 'AI', amount: 25_000_000, called: 25_000_000, distributed: 1_500_000, ownership: 40.3, status: 'confirmed', lastCallDate: '01/07/2024', contextId: null, commitmentType: 'co_investor' },
      { id: 3, investor: 'European Investment Bank',   initials: 'EI', amount: 17_000_000, called: 17_000_000, distributed: 1_000_000, ownership: 27.4, status: 'confirmed', lastCallDate: '01/07/2024', contextId: null, commitmentType: 'co_investor' },
    ],
    documents: [
      { id: 1, name: 'LPA – Meridian Solar Partners LP', ext: 'pdf',  size: '2.1 MB', updatedAt: '10/06/2024', version: '2.0', category: 'Legal',     access: 'accessible' },
      { id: 2, name: 'Financial Model – Meridian Solar', ext: 'xlsx', size: '4.3 MB', updatedAt: '15/09/2025', version: '5.1', category: 'Financial', access: 'accessible' },
      { id: 3, name: 'Q3 2025 Investor Report',          ext: 'pdf',  size: '1.6 MB', updatedAt: '15/10/2025', version: '1.0', category: 'Financial', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Prepare Q1 2026 investor report', priority: 'medium', status: 'in-progress', assignee: 'R. Chen',     dueDate: '20/04/2026' },
      { id: 2, title: 'Annual ESG metrics update',       priority: 'medium', status: 'todo',        assignee: 'A. Williams', dueDate: '30/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',    date: '12/06/2024', done: true },
      { label: 'First Close',        date: '12/06/2024', done: true },
      { label: 'Asset Acquired',     date: '01/08/2024', done: true },
      { label: 'First Distribution', date: '15/01/2025', done: true },
    ],
  },
  {
    id: 'spv-elbe',
    name: 'Elbe Biomass Infrastructure GmbH',
    status: 'active',
    jurisdiction: 'Germany',
    vehicleType: 'GmbH & Co. KG',
    established: '15/07/2024',
    sector: 'Renewable Energy',
    targetIRR: '13.5%',
    targetSize: 60_000_000,
    raisedAmount: 60_000_000,
    investorCount: 4,
    closingDate: '15/07/2024',
    dealId: 'deal-3',
    icSubmissionId: 'ic-elbe',
    assetId: 'elbe-biomass',
    fundId: 'fund-1',
    assetDataId: 'AST-006',
    gpFundAllocation: 10_000_000,  // GP's committed capital from Fund I to this SPV
    assetSnapshot: {
      name: 'Elbe Biomass Plant',
      sector: 'Renewable Energy',
      geography: 'Lower Saxony, Germany',
      operationalStatus: 'Operational',
      actualIRR: 13.1,
      targetIRR: '13.5%',
      valuation: '£61.4m',
      valuationDate: 'Sep 2025',
      esgScore: 71,
      riskScore: 3,
    },
    commitments: [
      { id: 1, investor: 'Meridian Capital Partners', initials: 'MC', amount: 22_000_000, called: 22_000_000, distributed: 2_100_000, ownership: 36.7, status: 'confirmed', lastCallDate: '15/08/2024', contextId: null, commitmentType: 'co_investor'   },
      { id: 2, investor: 'Caledonian Pension Trust',  initials: 'CP', amount: 18_000_000, called: 18_000_000, distributed: 1_700_000, ownership: 30.0, status: 'confirmed', lastCallDate: '15/08/2024', contextId: null, commitmentType: 'co_investor'   },
      { id: 3, investor: 'GP Co-Commitment',          initials: 'GR', amount: 10_000_000, called: 10_000_000, distributed:   950_000, ownership: 16.7, status: 'confirmed', lastCallDate: '15/08/2024', contextId: null, commitmentType: 'gp_allocation' },
      { id: 4, investor: 'Harrow DFI Mandate',        initials: 'HD', amount: 10_000_000, called: 10_000_000, distributed:   950_000, ownership: 16.6, status: 'confirmed', lastCallDate: '15/08/2024', contextId: null, commitmentType: 'co_investor'   },
    ],
    documents: [
      { id: 1, name: 'LPA – Elbe Biomass Infrastructure', ext: 'pdf',  size: '2.3 MB', updatedAt: '12/07/2024', version: '2.1', category: 'Legal',     access: 'accessible' },
      { id: 2, name: 'German Subsidy Framework Review',   ext: 'pdf',  size: '1.8 MB', updatedAt: '08/07/2024', version: '1.0', category: 'Legal',     access: 'restricted' },
      { id: 3, name: 'Q3 2025 Investor Report',           ext: 'pdf',  size: '1.4 MB', updatedAt: '15/10/2025', version: '1.0', category: 'Financial', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Annual subsidy regime compliance review', priority: 'high',   status: 'in-progress', assignee: 'S. Patel', dueDate: '15/04/2026' },
      { id: 2, title: 'Prepare Q1 2026 investor report',         priority: 'medium', status: 'todo',        assignee: 'R. Chen',  dueDate: '20/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',    date: '15/07/2024', done: true },
      { label: 'First Close',        date: '15/07/2024', done: true },
      { label: 'Asset Acquired',     date: '01/09/2024', done: true },
      { label: 'First Distribution', date: '15/03/2025', done: true },
    ],
  },
  {
    id: 'spv-avon',
    name: 'Avon Water Infrastructure LP',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '01/04/2025',
    sector: 'Utilities',
    targetIRR: '9.1%',
    targetSize: 48_000_000,
    raisedAmount: 48_000_000,
    investorCount: 3,
    closingDate: '01/04/2025',
    dealId: 'deal-6',
    icSubmissionId: null,
    assetId: 'avon-water',
    fundId: 'fund-2',
    assetDataId: 'AST-004',
    gpFundAllocation: 10_000_000,  // GP's committed capital from Fund II to this SPV
    assetSnapshot: {
      name: 'Avon Water Treatment Works',
      sector: 'Utilities',
      geography: 'South West England',
      operationalStatus: 'Operational',
      actualIRR: 9.3,
      targetIRR: '9.1%',
      valuation: '£49.5m',
      valuationDate: 'Dec 2025',
      esgScore: 68,
      riskScore: 2,
    },
    commitments: [
      { id: 1, investor: 'Nordic Pension Fund',       initials: 'NP', amount: 20_000_000, called: 20_000_000, distributed:   800_000, ownership: 41.7, status: 'confirmed', lastCallDate: '01/05/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 2, investor: 'Sovereign Wealth Partners', initials: 'SW', amount: 18_000_000, called: 18_000_000, distributed:   720_000, ownership: 37.5, status: 'confirmed', lastCallDate: '01/05/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 3, investor: 'GP Co-Commitment',          initials: 'GR', amount: 10_000_000, called: 10_000_000, distributed:   400_000, ownership: 20.8, status: 'confirmed', lastCallDate: '01/05/2025', contextId: null, commitmentType: 'gp_allocation' },
    ],
    documents: [
      { id: 1, name: 'LPA – Avon Water Infrastructure LP', ext: 'pdf',  size: '2.0 MB', updatedAt: '28/03/2025', version: '2.0', category: 'Legal',     access: 'accessible' },
      { id: 2, name: 'Ofwat Regulatory Compliance Pack',   ext: 'pdf',  size: '3.1 MB', updatedAt: '15/02/2025', version: '1.0', category: 'Legal',     access: 'restricted' },
      { id: 3, name: 'Q4 2025 Investor Report',            ext: 'pdf',  size: '1.3 MB', updatedAt: '20/01/2026', version: '1.0', category: 'Financial', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'AMP8 capital programme review',   priority: 'medium', status: 'in-progress', assignee: 'S. Patel', dueDate: '30/04/2026' },
      { id: 2, title: 'Prepare Q1 2026 investor report', priority: 'medium', status: 'todo',        assignee: 'R. Chen',  dueDate: '20/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',    date: '01/04/2025', done: true },
      { label: 'First Close',        date: '01/04/2025', done: true },
      { label: 'Asset Acquired',     date: '01/06/2025', done: true },
      { label: 'First Distribution', date: '15/10/2025', done: true },
    ],
  },
  {
    id: 'spv-porto',
    name: 'Porto Digital Hub SPV Ltd',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '20/05/2025',
    sector: 'Digital Infrastructure',
    targetIRR: '14.1%',
    targetSize: 45_000_000,
    raisedAmount: 45_000_000,
    investorCount: 4,
    closingDate: '20/05/2025',
    dealId: 'deal-10',
    icSubmissionId: 'ic-porto',
    assetId: 'porto-digital',
    fundId: 'fund-2',
    assetDataId: 'AST-005',
    gpFundAllocation: 8_000_000,  // GP's committed capital from Fund II to this SPV
    assetSnapshot: {
      name: 'Porto Digital Hub',
      sector: 'Digital Infrastructure',
      geography: 'Porto, Portugal',
      operationalStatus: 'Operational',
      actualIRR: 14.0,
      targetIRR: '14.1%',
      valuation: '£46.8m',
      valuationDate: 'Dec 2025',
      esgScore: 79,
      riskScore: 3,
    },
    commitments: [
      { id: 1, investor: 'Albion Infrastructure Fund', initials: 'AI', amount: 15_000_000, called: 15_000_000, distributed:   900_000, ownership: 33.3, status: 'confirmed', lastCallDate: '01/06/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 2, investor: 'Meridian Capital Partners',  initials: 'MC', amount: 12_000_000, called: 12_000_000, distributed:   720_000, ownership: 26.7, status: 'confirmed', lastCallDate: '01/06/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 3, investor: 'Caledonian Pension Trust',   initials: 'CP', amount: 10_000_000, called: 10_000_000, distributed:   600_000, ownership: 22.2, status: 'confirmed', lastCallDate: '01/06/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 4, investor: 'GP Co-Commitment',           initials: 'GR', amount:  8_000_000, called:  8_000_000, distributed:   480_000, ownership: 17.8, status: 'confirmed', lastCallDate: '01/06/2025', contextId: null, commitmentType: 'gp_allocation' },
    ],
    documents: [
      { id: 1, name: 'LPA – Porto Digital Hub SPV Ltd', ext: 'pdf',  size: '1.9 MB', updatedAt: '16/05/2025', version: '2.0', category: 'Legal',      access: 'accessible' },
      { id: 2, name: 'IC Approval Memo',                ext: 'pdf',  size: '1.1 MB', updatedAt: '28/03/2025', version: '1.0', category: 'Governance', access: 'restricted' },
      { id: 3, name: 'Q4 2025 Investor Report',         ext: 'pdf',  size: '1.5 MB', updatedAt: '20/01/2026', version: '1.0', category: 'Financial',  access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Anchor tenant contract renewal negotiation', priority: 'high',   status: 'in-progress', assignee: 'J. Donovan', dueDate: '15/04/2026' },
      { id: 2, title: 'Prepare Q1 2026 investor report',           priority: 'medium', status: 'todo',        assignee: 'R. Chen',    dueDate: '20/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',    date: '20/05/2025', done: true },
      { label: 'First Close',        date: '20/05/2025', done: true },
      { label: 'Asset Acquired',     date: '01/07/2025', done: true },
      { label: 'First Distribution', date: '15/12/2025', done: true },
    ],
  },
  {
    id: 'spv-lisbon',
    name: 'Lisbon Metro Concession SPV',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'PPP Concession Vehicle',
    established: '01/09/2025',
    sector: 'Transport Infrastructure',
    targetIRR: '10.2%',
    targetSize: 200_000_000,
    raisedAmount: 200_000_000,
    investorCount: 5,
    closingDate: '01/09/2025',
    dealId: 'deal-4',
    icSubmissionId: 'ic-lisbon',
    assetId: 'lisbon-metro',
    fundId: 'fund-2',
    assetDataId: 'AST-007',
    gpFundAllocation: 20_000_000,  // GP's committed capital from Fund II to this SPV
    assetSnapshot: {
      name: 'Lisbon Metro Extension',
      sector: 'Transport',
      geography: 'Greater Lisbon, Portugal',
      operationalStatus: 'In Construction',
      actualIRR: null,
      targetIRR: '10.2%',
      valuation: '£202m',
      valuationDate: 'Jan 2026',
      esgScore: 74,
      riskScore: 4,
    },
    capitalCall: {
      callNumber: 3,
      amount: 15_000_000,
      dueDate: '2026-04-30',
      status: 'issued',
      issuedDate: '2026-03-01',
    },
    commitments: [
      { id: 1, investor: 'Nordic Pension Fund',           initials: 'NP', amount: 60_000_000, called: 45_000_000, distributed: 0, ownership: 30.0, status: 'confirmed', lastCallDate: '01/11/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 2, investor: 'Abu Dhabi Investment Authority',initials: 'AI', amount: 50_000_000, called: 37_500_000, distributed: 0, ownership: 25.0, status: 'confirmed', lastCallDate: '01/11/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 3, investor: 'European Investment Bank',      initials: 'EI', amount: 40_000_000, called: 30_000_000, distributed: 0, ownership: 20.0, status: 'called',    lastCallDate: '01/03/2026', contextId: null, commitmentType: 'co_investor'   },
      { id: 4, investor: 'Sovereign Wealth Partners',     initials: 'SW', amount: 30_000_000, called: 22_500_000, distributed: 0, ownership: 15.0, status: 'confirmed', lastCallDate: '01/11/2025', contextId: null, commitmentType: 'co_investor'   },
      { id: 5, investor: 'GP Co-Commitment',              initials: 'GR', amount: 20_000_000, called: 15_000_000, distributed: 0, ownership: 10.0, status: 'confirmed', lastCallDate: '01/11/2025', contextId: null, commitmentType: 'gp_allocation' },
    ],
    documents: [
      { id: 1, name: 'PPP Concession Agreement',         ext: 'pdf',  size: '4.2 MB', updatedAt: '28/08/2025', version: '1.0', category: 'Legal',     access: 'restricted' },
      { id: 2, name: 'Construction Progress Report Q1',  ext: 'pdf',  size: '2.8 MB', updatedAt: '05/04/2026', version: '1.0', category: 'Technical', access: 'accessible' },
      { id: 3, name: 'Capital Call Notice No. 3',        ext: 'pdf',  size: '420 KB', updatedAt: '01/03/2026', version: '1.0', category: 'Financial', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Review contractor Q1 construction milestone report', priority: 'high',   status: 'in-progress', assignee: 'T. Hargreaves', dueDate: '20/04/2026' },
      { id: 2, title: 'Issue capital call notice No. 4 to LPs',            priority: 'high',   status: 'todo',        assignee: 'S. Patel',      dueDate: '30/06/2026' },
      { id: 3, title: 'Prepare Q1 2026 construction progress report',      priority: 'medium', status: 'todo',        assignee: 'R. Chen',       dueDate: '20/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',       date: '01/09/2025', done: true  },
      { label: 'First Close',           date: '01/09/2025', done: true  },
      { label: 'Construction Start',    date: '01/12/2025', done: true  },
      { label: 'Construction Complete', date: '01/06/2027', done: false },
      { label: 'Revenue Operations',    date: '01/09/2027', done: false },
    ],
  },
  {
    id: 'spv-nordic',
    name: 'Nordic Fibre Infrastructure Fund II LP',
    status: 'active',
    jurisdiction: 'England & Wales',
    vehicleType: 'Limited Partnership',
    established: '01/11/2025',
    sector: 'Digital Infrastructure',
    targetIRR: '11.8%',
    targetSize: 120_000_000,
    raisedAmount: 75_000_000,
    investorCount: 5,
    closingDate: '31/03/2027',
    dealId: 'deal-5',
    icSubmissionId: 'ic-nordic',
    assetId: 'nordic-fibre',
    fundId: 'fund-2',
    assetDataId: 'AST-008',
    gpFundAllocation: 10_000_000,  // GP's committed capital from Fund II to this SPV
    assetSnapshot: {
      name: 'Nordic Fibre Network',
      sector: 'Digital Infrastructure',
      geography: 'Sweden & Denmark',
      operationalStatus: 'In Construction',
      actualIRR: null,
      targetIRR: '11.8%',
      valuation: '£77m',
      valuationDate: 'Feb 2026',
      esgScore: 81,
      riskScore: 3,
    },
    capitalCall: {
      callNumber: 2,
      amount: 10_000_000,
      dueDate: '2026-05-15',
      status: 'issued',
      issuedDate: '2026-04-08',
    },
    commitments: [
      { id: 1, investor: 'Meridian Capital Partners',  initials: 'MC', amount: 30_000_000, called: 18_000_000, distributed: 0, ownership: 25.0, status: 'confirmed', lastCallDate: '08/04/2026', contextId: null, commitmentType: 'co_investor'   },
      { id: 2, investor: 'Nordic Pension Fund',        initials: 'NP', amount: 25_000_000, called: 15_000_000, distributed: 0, ownership: 20.8, status: 'confirmed', lastCallDate: '08/04/2026', contextId: null, commitmentType: 'co_investor'   },
      { id: 3, investor: 'Sovereign Wealth Partners',  initials: 'SW', amount: 20_000_000, called: 12_000_000, distributed: 0, ownership: 16.7, status: 'called',    lastCallDate: '08/04/2026', contextId: null, commitmentType: 'co_investor'   },
      { id: 4, investor: 'Westgate Endowment',         initials: 'WE', amount: 10_000_000, called:  6_000_000, distributed: 0, ownership:  8.3, status: 'pending',   lastCallDate: '—',          contextId: null, commitmentType: 'co_investor'   },
      { id: 5, investor: 'GP Co-Commitment',           initials: 'GR', amount: 10_000_000, called:  6_000_000, distributed: 0, ownership:  8.3, status: 'confirmed', lastCallDate: '08/04/2026', contextId: null, commitmentType: 'gp_allocation' },
    ],
    documents: [
      { id: 1, name: 'LPA – Nordic Fibre Fund II LP',       ext: 'pdf',  size: '2.2 MB', updatedAt: '28/10/2025', version: '1.0', category: 'Legal',     access: 'accessible' },
      { id: 2, name: 'Capital Call Notice No. 2',           ext: 'pdf',  size: '410 KB', updatedAt: '08/04/2026', version: '1.0', category: 'Financial', access: 'accessible' },
      { id: 3, name: 'Q1 2026 Construction Progress Brief', ext: 'pdf',  size: '1.7 MB', updatedAt: '05/04/2026', version: '1.0', category: 'Technical', access: 'accessible' },
    ],
    tasks: [
      { id: 1, title: 'Confirm receipt of capital call No. 2 from Westgate', priority: 'high',   status: 'todo',        assignee: 'S. Patel',      dueDate: '15/05/2026' },
      { id: 2, title: 'Update fibre rollout programme schedule',             priority: 'medium', status: 'in-progress', assignee: 'T. Hargreaves', dueDate: '30/04/2026' },
      { id: 3, title: 'Prepare Q1 2026 investor brief',                     priority: 'medium', status: 'todo',        assignee: 'R. Chen',       dueDate: '20/04/2026' },
    ],
    milestones: [
      { label: 'SPV Established',       date: '01/11/2025', done: true  },
      { label: 'First Close',           date: '01/11/2025', done: true  },
      { label: 'Rollout Phase 1 Start', date: '01/01/2026', done: true  },
      { label: 'Final Close',           date: '31/03/2027', done: false },
      { label: 'Network Complete',      date: '01/06/2027', done: false },
    ],
  },
];

// ─── External Deal Room — Deals ───────────────────────────────────────────────
// Used by DealRoomPage (external investor / co-investor view).
// contexts: demo context IDs that can see this deal room entry.
//           If absent, visible to all external roles (and platform_admin).

export const MOCK_DEAL_ROOM_DEALS = [
  {
    id: 'dr-solano',
    name: 'Solano Wind Farm SPV',
    sector: 'Renewable Energy',
    targetSize: '£85m',
    accessState: MARKETPLACE_STATES.DD_ACCESS,
    contexts: ['ctx-platform-admin'],
  },
  {
    id: 'dr-porto',
    name: 'Porto Digital Hub SPV',
    sector: 'Digital Infrastructure',
    targetSize: '£45m',
    accessState: MARKETPLACE_STATES.NDA_PENDING,
    contexts: ['ctx-external-investor', 'ctx-platform-admin'],
  },
  {
    id: 'dr-nordic',
    name: 'Nordic Fibre Network — Co-Investment',
    sector: 'Digital Infrastructure',
    targetSize: '£120m',
    targetIRR: '11.8%',
    minTicket: '£20m',
    accessState: MARKETPLACE_STATES.DD_ACCESS,
    contexts: ['ctx-external-investor', 'ctx-platform-admin'],
  },
  {
    id: 'dr-elbe',
    name: 'Elbe Biomass Plant SPV',
    sector: 'Renewable Energy',
    targetSize: '£60m',
    accessState: MARKETPLACE_STATES.COMMITTED,
    contexts: ['ctx-external-investor', 'ctx-platform-admin'],
  },
  {
    id: 'dr-lisbon',
    name: 'Lisbon Metro Extension',
    sector: 'Transport Infrastructure',
    targetSize: '£200m',
    accessState: MARKETPLACE_STATES.ACCESS_REQUESTED,
    contexts: ['ctx-external-investor', 'ctx-platform-admin'],
  },
  {
    id: 'dr-iberian',
    name: 'Iberian Battery Storage Complex',
    sector: 'Energy Transition',
    targetSize: '£90m',
    targetIRR: '13.8%',
    minTicket: '£7.5m',
    accessState: MARKETPLACE_STATES.DD_ACCESS,
    contexts: ['ctx-co-investor', 'ctx-platform-admin'],
    coInvestmentTerms: {
      allocation: '£20m (22.2% of equity)',
      carry: '15% above 8% hurdle',
      preferredReturn: '8% p.a. cumulative',
      managementFee: '1.5% on committed capital',
      governanceRights: 'Observer seat on SPV Advisory Board',
      closingDate: '30 Sep 2026',
    },
  },
];

// ─── External Deal Room — Documents ──────────────────────────────────────────
// tier: 'teaser' = visible after access request; 'dd' = requires DD_ACCESS or above.

export const MOCK_DEAL_ROOM_DOCUMENTS = [
  // Solano — DD_ACCESS (co-investor, full access)
  { id: 'dr-doc-1', dealId: 'dr-solano', name: 'Investment Teaser',              ext: 'pdf',  size: '1.2 MB', category: 'Marketing', tier: 'teaser' },
  { id: 'dr-doc-2', dealId: 'dr-solano', name: 'Information Memorandum',         ext: 'pdf',  size: '4.8 MB', category: 'Legal',     tier: 'dd'     },
  { id: 'dr-doc-3', dealId: 'dr-solano', name: 'Financial Model — Base Case',    ext: 'xlsx', size: '5.1 MB', category: 'Financial', tier: 'dd'     },
  { id: 'dr-doc-4', dealId: 'dr-solano', name: 'Technical Due Diligence Report', ext: 'pdf',  size: '3.4 MB', category: 'Technical', tier: 'dd'     },
  { id: 'dr-doc-5', dealId: 'dr-solano', name: 'ESG Impact Assessment',          ext: 'pdf',  size: '2.1 MB', category: 'ESG',       tier: 'dd'     },
  // Porto — NDA_PENDING (teaser only)
  { id: 'dr-doc-6', dealId: 'dr-porto',  name: 'Investment Teaser',              ext: 'pdf',  size: '980 KB', category: 'Marketing', tier: 'teaser' },
  { id: 'dr-doc-7', dealId: 'dr-porto',  name: 'Information Memorandum',         ext: 'pdf',  size: '3.9 MB', category: 'Legal',     tier: 'dd'     },
  { id: 'dr-doc-8', dealId: 'dr-porto',  name: 'Financial Projections',          ext: 'xlsx', size: '2.7 MB', category: 'Financial', tier: 'dd'     },
  // Nordic Fibre — professional GP co-investment framing (GlobalInfra Partners)
  { id: 'dr-doc-9',  dealId: 'dr-nordic', name: 'Co-Investment Teaser',                          ext: 'pdf',  size: '1.1 MB', category: 'Co-Investment Overview', tier: 'teaser' },
  { id: 'dr-doc-10', dealId: 'dr-nordic', name: 'Executive Summary',                              ext: 'pdf',  size: '2.4 MB', category: 'Co-Investment Overview', tier: 'teaser' },
  { id: 'dr-doc-11', dealId: 'dr-nordic', name: 'Deal Structure Overview',                        ext: 'pdf',  size: '980 KB', category: 'Co-Investment Overview', tier: 'teaser' },
  { id: 'dr-doc-12', dealId: 'dr-nordic', name: 'Financial Model — Base & Downside Cases',        ext: 'xlsx', size: '4.8 MB', category: 'Financial Due Diligence', tier: 'dd'     },
  { id: 'dr-doc-13', dealId: 'dr-nordic', name: 'Technical Due Diligence Report',                 ext: 'pdf',  size: '6.2 MB', category: 'Technical Due Diligence', tier: 'dd'     },
  { id: 'dr-doc-14', dealId: 'dr-nordic', name: 'Co-Investment Agreement Template',               ext: 'docx', size: '1.3 MB', category: 'Legal & Governance',     tier: 'dd'     },
  { id: 'dr-doc-15', dealId: 'dr-nordic', name: 'ESG & Regulatory Assessment',                    ext: 'pdf',  size: '3.1 MB', category: 'ESG & Compliance',       tier: 'dd'     },
  { id: 'dr-doc-16', dealId: 'dr-nordic', name: 'Legal Structure Pack',                           ext: 'pdf',  size: '2.7 MB', category: 'Legal & Governance',     tier: 'dd'     },
  // Elbe — COMMITTED (all docs accessible)
  { id: 'dr-doc-17', dealId: 'dr-elbe',   name: 'Investment Teaser',              ext: 'pdf',  size: '880 KB', category: 'Marketing', tier: 'teaser' },
  { id: 'dr-doc-18', dealId: 'dr-elbe',   name: 'Information Memorandum',         ext: 'pdf',  size: '3.7 MB', category: 'Legal',     tier: 'dd'     },
  { id: 'dr-doc-19', dealId: 'dr-elbe',   name: 'Subscription Agreement',         ext: 'pdf',  size: '1.1 MB', category: 'Legal',     tier: 'dd'     },
  { id: 'dr-doc-20', dealId: 'dr-elbe',   name: 'LP Capital Call Schedule',       ext: 'xlsx', size: '420 KB', category: 'Financial', tier: 'dd'     },
  // Lisbon — ACCESS_REQUESTED (teaser only)
  { id: 'dr-doc-21', dealId: 'dr-lisbon',  name: 'Investment Teaser',                              ext: 'pdf',  size: '1.4 MB', category: 'Marketing',              tier: 'teaser' },
  { id: 'dr-doc-22', dealId: 'dr-lisbon',  name: 'Project Overview Presentation',                  ext: 'pdf',  size: '2.1 MB', category: 'Marketing',              tier: 'teaser' },
  // Iberian Battery Storage — DD_ACCESS (co-investor, full access)
  { id: 'dr-doc-23', dealId: 'dr-iberian', name: 'Co-Investment Teaser',                           ext: 'pdf',  size: '1.0 MB', category: 'Co-Investment Overview', tier: 'teaser' },
  { id: 'dr-doc-24', dealId: 'dr-iberian', name: 'Information Memorandum',                         ext: 'pdf',  size: '4.2 MB', category: 'Legal',                  tier: 'dd'     },
  { id: 'dr-doc-25', dealId: 'dr-iberian', name: 'Financial Model — Base & Downside Cases',        ext: 'xlsx', size: '4.3 MB', category: 'Financial Due Diligence', tier: 'dd'     },
  { id: 'dr-doc-26', dealId: 'dr-iberian', name: 'Co-Investment Agreement Template',               ext: 'docx', size: '1.1 MB', category: 'Legal & Governance',     tier: 'dd'     },
  { id: 'dr-doc-27', dealId: 'dr-iberian', name: 'ESG & Capacity Market Assessment',               ext: 'pdf',  size: '2.7 MB', category: 'ESG & Compliance',       tier: 'dd'     },
  { id: 'dr-doc-28', dealId: 'dr-iberian', name: 'Technical Due Diligence — BESS Technology',     ext: 'pdf',  size: '5.1 MB', category: 'Technical Due Diligence', tier: 'dd'     },
];

// ─── Activity Feed ────────────────────────────────────────────────────────────
// roleTarget:  array of role strings — if present, item is only shown to those roles.
//              Internal roles: use ROLES.* values ('investment_team', 'ic_member', etc.)
//              If absent, no role filter applies.
// contextIds:  array of demo context IDs — if present, the active context.id must match.
//              Generic rule: if roleTarget exists, role must match.
//              If contextIds exists, activeContext.id must match.
//              If contextIds is absent, falls back to roleTarget only.

export const MOCK_ACTIVITY_FEED = [
  // ── Today ──────────────────────────────────────────────────────────────────
  // Thread A: Tyne Bridge / IC governance
  {
    id: 'af-A1',
    type: 'ic',
    dateLabel: 'Today',
    time: '10:30',
    title: 'Tyne Bridge — Tom Whitfield voted Approved',
    description: 'Tom Whitfield (Sector Lead — Transport) submitted Approved vote for Tyne Bridge Refinancing IC submission. Vote tally: 2 of 4. James Donovan and Rachel Moore votes outstanding ahead of 13 April committee meeting.',
    actor: 'T. Whitfield',
    tags: ['IC Vote', 'Tyne Bridge', 'Fund II'],
    roleTarget: ['investment_team', 'ic_member', 'platform_admin'],
  },
  {
    id: 'af-A2',
    type: 'deal',
    dateLabel: 'Today',
    time: '09:15',
    title: 'Fund II pipeline — Tyne Bridge flagged at IC Review',
    description: 'Tyne Bridge Refinancing (deal-7) flagged as priority in Fund II pipeline. IC meeting confirmed for 13 April. SPV in pre-formation; 3 anchor investors soft-circled pending IC outcome.',
    actor: 'System',
    tags: ['Fund II', 'Pipeline', 'IC Review'],
    roleTarget: ['investment_team', 'platform_admin'],
  },
  // Thread B: GlobalInfra Partners / Nordic Fibre
  {
    id: 'af-B1',
    type: 'document',
    dateLabel: 'Today',
    time: '08:45',
    title: 'Nordic Fibre Network — Co-investment data room unlocked',
    description: 'Full due diligence package now accessible following NDA execution. Five DD documents available: Financial Model, Technical Due Diligence Report, Co-Investment Agreement Template, ESG & Regulatory Assessment, Legal Structure Pack.',
    actor: 'SeedDrive Platform',
    tags: ['DD Access', 'Nordic Fibre', 'Co-Investment'],
    roleTarget: ['external_investor'],
    contextIds: ['ctx-external-investor'],
  },
  // Thread C: Apex Infrastructure Partners / Solano Wind
  {
    id: 'af-C1',
    type: 'spv',
    dateLabel: 'Today',
    time: '08:30',
    title: 'Solano Wind — Capital Call No. 3 receipt confirmed by Apex',
    description: 'Apex Infrastructure Partners confirmed receipt of Capital Call Notice No. 3 (£2.1m due 30 April 2026). Confirmation logged against co-investment position.',
    actor: 'Apex Infrastructure Partners',
    tags: ['Capital Call', 'Solano Wind', 'Co-Investment'],
    roleTarget: ['investment_team', 'finance_team', 'platform_admin', 'co_investor'],
    contextIds: ['ctx-co-investor'],
  },
  // Thread E: Asset Management / Q1 review
  {
    id: 'af-E1',
    type: 'asset',
    dateLabel: 'Today',
    time: '08:00',
    title: 'Solano Wind Q1 2026 — Oversight sign-off pending: Portfolio Director',
    description: 'Q1 2026 oversight review awaiting final sign-off from James Harrison (Portfolio Director). Priya Sharma (submitted 08 Apr) and Tom Hargreaves (signed 09 Apr) sign-offs complete.',
    actor: 'System',
    tags: ['Q1 Review', 'Solano Wind', 'Oversight'],
    roleTarget: ['asset_mgmt_team', 'finance_team', 'platform_admin'],
  },
  // Thread D: Deal Originator
  {
    id: 'af-D1',
    type: 'deal',
    dateLabel: 'Today',
    time: '07:50',
    title: 'Lagos Solar Platform — Assigned for screening review',
    description: 'Your Phase I submission has been logged and assigned to James Harrison for initial screening review. Reference: deal-11. You will be notified when the screening decision is made.',
    actor: 'SeedDrive Platform',
    tags: ['Submission', 'Lagos Solar', 'Sourcing'],
    roleTarget: ['deal_originator'],
    contextIds: ['ctx-deal-originator'],
  },

  // ── Yesterday ──────────────────────────────────────────────────────────────
  // Thread A
  {
    id: 'af-A3',
    type: 'ic',
    dateLabel: 'Yesterday',
    time: '16:30',
    title: 'Tyne Bridge — Sarah Chen voted Approved (IC Chair)',
    description: 'Sarah Chen (IC Chair) submitted Approved vote for Tyne Bridge Refinancing. First vote received. Vote tally now 1 of 4.',
    actor: 'S. Chen',
    tags: ['IC Vote', 'Tyne Bridge'],
    roleTarget: ['investment_team', 'ic_member', 'platform_admin'],
  },
  {
    id: 'af-A4',
    type: 'spv',
    dateLabel: 'Yesterday',
    time: '15:00',
    title: 'Tyne Bridge — SPV record created in pre-formation',
    description: 'Tyne Bridge Refinancing SPV record created. Status: pre-formation. Three anchor investors soft-circled (indicative only). Formal close opens upon IC approval.',
    actor: 'J. Donovan',
    tags: ['SPV', 'Tyne Bridge', 'Pre-Formation'],
    roleTarget: ['investment_team', 'platform_admin'],
  },
  // Thread B
  {
    id: 'af-B2',
    type: 'document',
    dateLabel: 'Yesterday',
    time: '14:30',
    title: 'Nordic Fibre Network — NDA executed by GlobalInfra Partners',
    description: 'NDA confirmed. Co-investment data room access granted. Due diligence documents now available.',
    actor: 'GlobalInfra Partners',
    tags: ['NDA', 'Nordic Fibre', 'Co-Investment'],
    roleTarget: ['external_investor'],
    contextIds: ['ctx-external-investor'],
  },
  // Thread C
  {
    id: 'af-C2',
    type: 'document',
    dateLabel: 'Yesterday',
    time: '14:00',
    title: 'Solano Wind — Q1 2026 Co-Investor Report distributed',
    description: 'Q1 2026 Co-Investor Report distributed to Apex Infrastructure Partners. Q1 availability: 97.2%. Capex programme 3 weeks behind schedule — contractor review underway. IRR tracking on plan.',
    actor: 'P. Sharma',
    tags: ['Q1 Report', 'Solano Wind', 'Co-Investment'],
    roleTarget: ['investment_team', 'finance_team', 'platform_admin', 'co_investor'],
    contextIds: ['ctx-co-investor'],
  },
  // Thread D
  {
    id: 'af-D2',
    type: 'deal',
    dateLabel: 'Yesterday',
    time: '12:00',
    title: 'Kano Wind Corridor — Progressed to Screening',
    description: 'Your submission has been reviewed and progressed to Screening stage. Assigned analyst: Sarah Mitchell.',
    actor: 'System',
    tags: ['Stage Progress', 'Kano Wind Corridor'],
    roleTarget: ['deal_originator'],
    contextIds: ['ctx-deal-originator'],
  },
  // Thread E
  {
    id: 'af-E2',
    type: 'asset',
    dateLabel: 'Yesterday',
    time: '11:00',
    title: 'Solano Wind Q1 2026 — Sector sign-off: Tom Hargreaves',
    description: 'Tom Hargreaves (Head of Renewables) signed off Q1 2026 review for Solano Wind Infrastructure. Capex delay action logged: contractor programme review due 30 April. Forwarded to Portfolio Director.',
    actor: 'T. Hargreaves',
    tags: ['Q1 Review', 'Solano Wind', 'Oversight'],
    roleTarget: ['asset_mgmt_team', 'finance_team', 'platform_admin'],
  },

  // ── 09 Apr 2026 ────────────────────────────────────────────────────────────
  // Thread A
  {
    id: 'af-A5',
    type: 'ic',
    dateLabel: '09 Apr 2026',
    time: '15:30',
    title: 'Tyne Bridge Refinancing — IC submission lodged',
    description: 'T. Hargreaves lodged IC submission for Tyne Bridge Refinancing (Fund II). IC meeting date: 13 April 2026. Risk flags: capex overrun £2.1m (4.4%), contractor timeline buffer risk.',
    actor: 'T. Hargreaves',
    tags: ['IC Submission', 'Tyne Bridge', 'Fund II'],
    roleTarget: ['investment_team', 'ic_member', 'platform_admin'],
  },
  // Thread B
  {
    id: 'af-B3',
    type: 'deal',
    dateLabel: '09 Apr 2026',
    time: '10:30',
    title: 'Nordic Fibre Network — Co-investment access requested',
    description: 'GlobalInfra Partners submitted a co-investment access request for Nordic Fibre Network. Teaser documents available. NDA required for full data room access.',
    actor: 'GlobalInfra Partners',
    tags: ['Access Request', 'Nordic Fibre'],
    roleTarget: ['external_investor'],
    contextIds: ['ctx-external-investor'],
  },
  // Thread C
  {
    id: 'af-C3',
    type: 'spv',
    dateLabel: '09 Apr 2026',
    time: '09:45',
    title: 'Solano Wind — Capital Call No. 3 issued: £2.1m due 30 April',
    description: 'Capital Call Notice No. 3 issued to Apex Infrastructure Partners. Amount: £2.1m. Due date: 30 April 2026. Q1 2026 Co-Investor Report distributed alongside call notice.',
    actor: 'R. Chen',
    tags: ['Capital Call', 'Solano Wind'],
    roleTarget: ['investment_team', 'finance_team', 'platform_admin', 'co_investor'],
    contextIds: ['ctx-co-investor'],
  },
  // Thread D
  {
    id: 'af-D3',
    type: 'deal',
    dateLabel: '09 Apr 2026',
    time: '09:00',
    title: 'Lagos Solar Platform — Phase I submission received',
    description: 'New deal originator submission logged. Lagos Solar Platform Phase I (50MW, Lagos State). Assigned to James Harrison for initial screening.',
    actor: 'SeedDrive Platform',
    tags: ['New Submission', 'Lagos Solar'],
    roleTarget: ['deal_originator'],
    contextIds: ['ctx-deal-originator'],
  },
  // Thread E
  {
    id: 'af-E3',
    type: 'asset',
    dateLabel: '09 Apr 2026',
    time: '08:30',
    title: 'Solano Wind Q1 2026 — Review pack submitted by Priya Sharma',
    description: 'Q1 2026 operational review pack submitted by Priya Sharma (Asset Manager). Key flags: availability 97.2% (above contracted minimum); capex programme 3 weeks behind schedule. Risk score updated to 3.',
    actor: 'P. Sharma',
    tags: ['Q1 Review', 'Solano Wind', 'Asset Management'],
    roleTarget: ['asset_mgmt_team', 'finance_team', 'platform_admin'],
  },

  // ── 08 Apr 2026 ────────────────────────────────────────────────────────────
  // Thread A
  {
    id: 'af-A6',
    type: 'deal',
    dateLabel: '08 Apr 2026',
    time: '16:00',
    title: 'Fund II pipeline — Tyne Bridge progressed to IC Review',
    description: 'Tyne Bridge Refinancing progressed from Diligence to IC Review in Fund II pipeline. Deal flagged as priority. 14 days in current stage.',
    actor: 'T. Hargreaves',
    tags: ['Fund II', 'Pipeline', 'Tyne Bridge'],
    roleTarget: ['investment_team', 'platform_admin'],
  },
  // Thread B
  {
    id: 'af-B4',
    type: 'deal',
    dateLabel: '08 Apr 2026',
    time: '14:00',
    title: 'Nordic Fibre Network — Co-investment teaser reviewed',
    description: 'Co-Investment Teaser, Executive Summary, and Deal Structure Overview accessed. Opportunity summary: £120m target, 11.8% target IRR, £20m minimum ticket.',
    actor: 'GlobalInfra Partners',
    tags: ['Teaser Review', 'Nordic Fibre'],
    roleTarget: ['external_investor'],
    contextIds: ['ctx-external-investor'],
  },
  // Thread C
  {
    id: 'af-C4',
    type: 'spv',
    dateLabel: '08 Apr 2026',
    time: '11:30',
    title: 'Solano Wind — Q4 2025 distribution settled: £650K to Apex',
    description: 'Q4 2025 distribution settled. Apex Infrastructure Partners received £650K net of withholding. Cumulative distributions to Apex: £650K since SPV inception.',
    actor: 'R. Chen',
    tags: ['Distribution', 'Solano Wind', 'Co-Investment'],
    roleTarget: ['investment_team', 'finance_team', 'platform_admin', 'co_investor'],
    contextIds: ['ctx-co-investor'],
  },
  // Thread E
  {
    id: 'af-E4',
    type: 'asset',
    dateLabel: '08 Apr 2026',
    time: '09:00',
    title: 'Solano Wind — Risk score updated to 3 (capex monitoring flag)',
    description: 'Risk score updated from 2 to 3. Trigger: capex programme running 3 weeks behind schedule due to contractor resourcing constraints. Agreed action: revised contractor programme review by 30 April.',
    actor: 'P. Sharma',
    tags: ['Risk Update', 'Solano Wind', 'Capex'],
    roleTarget: ['asset_mgmt_team', 'finance_team', 'platform_admin'],
  },

  // ── 07 Apr 2026 ────────────────────────────────────────────────────────────
  // Thread A
  {
    id: 'af-A7',
    type: 'ic',
    dateLabel: '07 Apr 2026',
    time: '15:00',
    title: 'Tyne Bridge — IC submission confirmed for Q2 2026 calendar',
    description: 'Tyne Bridge Refinancing IC submission confirmed for 13 April 2026 committee meeting. Submission pack distributed to committee members: Sarah Chen, Tom Whitfield, James Donovan, Rachel Moore.',
    actor: 'J. Donovan',
    tags: ['IC Calendar', 'Tyne Bridge'],
    roleTarget: ['investment_team', 'ic_member', 'platform_admin'],
  },
  // Thread C
  {
    id: 'af-C5',
    type: 'document',
    dateLabel: '07 Apr 2026',
    time: '09:30',
    title: 'Solano Wind — Q1 2026 co-investor portfolio pack available',
    description: 'Q1 2026 Co-Investor Report prepared and ready for distribution. Key metrics: availability 97.2%, IRR tracking on plan, Q1 capex programme under review.',
    actor: 'P. Sharma',
    tags: ['Q1 Report', 'Solano Wind'],
    roleTarget: ['investment_team', 'finance_team', 'platform_admin', 'co_investor'],
    contextIds: ['ctx-co-investor'],
  },
  // Thread E
  {
    id: 'af-E5',
    type: 'asset',
    dateLabel: '07 Apr 2026',
    time: '08:30',
    title: 'Solano Wind — Q1 2026 review pack opened for asset management',
    description: 'Q1 2026 review period opened for Solano Wind Infrastructure. Priya Sharma assigned as review lead. Key monitoring flag: capex programme — contractor resourcing constraints noted.',
    actor: 'System',
    tags: ['Q1 Review', 'Solano Wind', 'Asset Management'],
    roleTarget: ['asset_mgmt_team', 'finance_team', 'platform_admin'],
  },
];

// ─── Search Stubs ─────────────────────────────────────────────────────────────
// Used by GlobalHeader spotlight search. Minimal stubs — search categories exist
// but surface no results (pipeline deals and SPV documents are the authoritative sources).

export const MOCK_DEALS = [];
export const MOCK_DOCUMENTS = [];
export const MOCK_PEOPLE = [];
